import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
	SlidersHorizontal,
	ChevronDown,
	ChevronUp,
	ShieldCheck,
	Clock,
	X,
	History,
	Search,
	AlertCircle,
	Bell,
	RefreshCw,
	Users,
	Timer,
	Layers,
	BellOff,
} from 'lucide-react';
import SettingHeaderComponent from '../../components/settingHeaderComponent';
import {
	getNotificationPreferences,
	updateNotificationPreferences,
	triggerTestNotification,
	getNotificationHistory,
	getSnoozedBatches,
	unsnoozeBatch,
} from '../../../../../services/settings/preferences';
import './notifications.scss';

export const TRIGGER_TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
	const totalMinutes = i * 30;
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	const hh = String(hours).padStart(2, '0');
	const mm = String(minutes).padStart(2, '0');
	const value = `${hh}:${mm}`;
	const suffix = hours >= 12 ? 'PM' : 'AM';
	const h12 = hours % 12 === 0 ? 12 : hours % 12;
	const label12 = `${String(h12).padStart(2, '0')}:${mm} ${suffix}`;
	return {
		value,
		label: `${label12} (${value})`,
	};
});

export const normalizeTimeValue = (val) => {
	if (!val) return '09:00';
	const m = String(val).trim().match(/^(\d{1,2}):(\d{2})/);
	if (!m) return '09:00';
	return `${String(m[1]).padStart(2, '0')}:${m[2]}`;
};

export const ALL_COOLDOWN_OPTIONS = [
	{ value: 'none', label: 'None (Send Every Event / Routine)' },
	{ value: '30m', label: '30 Minutes' },
	{ value: '1h', label: '1 Hour' },
	{ value: '6h', label: '6 Hours' },
	{ value: '12h', label: '12 Hours' },
	{ value: '24h', label: '1 Day (24 Hours)' },
	{ value: '3d', label: '3 Days' },
	{ value: '7d', label: '7 Days' },
	{ value: '30d', label: '30 Days' },
];

export const getCorrelatedCooldownOptions = (rule) => {
	const isScheduled =
		rule?.triggerType === 'scheduled' ||
		rule?.triggerType === 'both' ||
		/^.+_job$/.test(rule?.id || '');
	const freq = (rule?.frequency || 'daily').toLowerCase();

	if (!isScheduled) {
		return ALL_COOLDOWN_OPTIONS;
	}

	if (freq === 'hourly') {
		return ALL_COOLDOWN_OPTIONS.filter((opt) =>
			['none', '30m', '1h'].includes(opt.value),
		);
	}
	if (freq === 'daily') {
		return ALL_COOLDOWN_OPTIONS.filter((opt) =>
			['none', '30m', '1h', '6h', '12h', '24h', '3d'].includes(opt.value),
		);
	}
	if (freq === 'weekly') {
		return ALL_COOLDOWN_OPTIONS.filter((opt) =>
			['none', '30m', '1h', '6h', '12h', '24h', '3d', '7d'].includes(opt.value),
		);
	}

	return ALL_COOLDOWN_OPTIONS;
};

export const formatLeadDaysLabel = (leadDays, ruleId = '') => {
	if (!Array.isArray(leadDays) || !leadDays.length) return '';
	const sorted = [...leadDays].sort((a, b) => b - a);
	const isExpiry = String(ruleId || '').toLowerCase().includes('expiry');
	const targetLabel = isExpiry ? 'expiry' : 'due date';
	const daysText = sorted.length === 1 && sorted[0] === 1 ? '1 day' : `${sorted.join(', ')} days`;
	return `${daysText} before ${targetLabel}`;
};

export const getDisplayScheduleText = (rule) => {
	if (!rule) return '';
	if (rule.triggerType === 'immediate' || rule.triggerType === 'event') return 'Instant on event';

	const timeStr = normalizeTimeValue(rule.triggerTime);
	const [hh, mm] = timeStr.split(':').map((n) => parseInt(n, 10));
	const suffix = hh >= 12 ? 'PM' : 'AM';
	const h12 = hh % 12 === 0 ? 12 : hh % 12;
	const formattedTime = `${h12}:${String(mm).padStart(2, '0')} ${suffix}`;

	const freq = (rule.frequency || 'daily').toLowerCase();
	let base;
	if (freq === 'hourly') {
		base = `Every hour at :${String(mm).padStart(2, '0')}`;
	} else if (freq === 'weekly') {
		const weekdayNames = [
			'Sunday',
			'Monday',
			'Tuesday',
			'Wednesday',
			'Thursday',
			'Friday',
			'Saturday',
		];
		const wdName = weekdayNames[Number.isInteger(rule.weekday) ? rule.weekday : 1];
		base = `Weekly on ${wdName} at ${formattedTime}`;
	} else {
		const freqLabel = freq.charAt(0).toUpperCase() + freq.slice(1);
		base = `${freqLabel} at ${formattedTime}`;
	}

	const lead =
		Array.isArray(rule.leadDays) && rule.leadDays.length
			? ` · ${formatLeadDaysLabel(rule.leadDays, rule.id)}`
			: '';

	if (rule.triggerType === 'both') {
		return `Instant on event & ${base}${lead}`;
	}
	return `${base}${lead}`;
};

/**
 * Checks whether test notification triggers should be visible.
 * Permitted strictly in localhost, dev, and uat environments; hidden in production.
 */
export const canShowTestAlert = () => {
	if (typeof window === 'undefined') return false;
	const hostname = window.location.hostname.toLowerCase();

	// Localhost / Loopback / LAN
	if (
		hostname === 'localhost' ||
		hostname === '127.0.0.1' ||
		hostname === '0.0.0.0' ||
		hostname.endsWith('.local') ||
		hostname.startsWith('192.168.') ||
		hostname.startsWith('10.')
	) {
		return true;
	}

	// Dev, UAT, or Staging hostnames
	if (
		hostname.includes('dev.') ||
		hostname.includes('dev-') ||
		hostname.includes('-dev') ||
		hostname.includes('.dev') ||
		hostname.includes('uat.') ||
		hostname.includes('uat-') ||
		hostname.includes('-uat') ||
		hostname.includes('.uat') ||
		hostname.includes('devoyssh') ||
		hostname.includes('staging')
	) {
		return true;
	}

	// Fallback to environment variable if not running on the production domain
	const env = (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase();
	if (['development', 'dev', 'uat', 'local', 'staging'].includes(env)) {
		if (!hostname.includes('app.pharmnex.app')) {
			return true;
		}
	}

	return false;
};

export default function NotificationSettings() {
	const user = useSelector((state) => state?.userInfo?.userInfo?.data);
	const showTestAlert = useMemo(() => canShowTestAlert(), []);

	// Check if user is Organisation Owner or has permission to edit settings
	const isOrgOwner = useMemo(() => {
		return (
			user?.role?.key === 'org_owner' ||
			user?.is_owner === true ||
			user?.user_type_id === 1
		);
	}, [user]);

	const canEdit = useMemo(() => {
		if (isOrgOwner) return true;
		const userPermissions = user?.role?.role_permissions || [];
		return (
			userPermissions.includes('settings.notification_preferences.edit') ||
			userPermissions.includes('settings.general_preferences.edit')
		);
	}, [isOrgOwner, user]);

	const [activeTab, setActiveTab] = useState('rules'); // 'rules' | 'channels' | 'schedules' | 'snoozed' | 'history'
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [testingRuleId, setTestingRuleId] = useState(null);

	// Core Preferences State
	const [rules, setRules] = useState([]);
	const [channels, setChannels] = useState({
		in_app: true,
		email: true,
		customer_email: true,
		vendor_email: true,
		org_user_email: true,
	});
	const [roleMatrix, setRoleMatrix] = useState({});
	const [quietHours, setQuietHours] = useState({
		enabled: true,
		start: '22:00',
		end: '07:00',
	});
	const [schedules, setSchedules] = useState([]);
	// The organisation's own time zone: alerts, quiet hours and schedules all run on it.
	const [orgTimezone, setOrgTimezone] = useState(null);
	const [availableRoles, setAvailableRoles] = useState([]);

	// History State
	const [historyLogs, setHistoryLogs] = useState([]);
	const [loadingHistory, setLoadingHistory] = useState(false);

	// Snoozed Alerts State (batches whose expiry alerts are paused)
	const [snoozedBatches, setSnoozedBatches] = useState([]);
	const [loadingSnoozed, setLoadingSnoozed] = useState(false);
	const [unsnoozingId, setUnsnoozingId] = useState(null);

	// Filters & Drawer State
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedModule, setSelectedModule] = useState('all');
	const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'enabled' | 'disabled'
	const [collapsedModules, setCollapsedModules] = useState({});
	const [drawerRule, setDrawerRule] = useState(null); // When set, configure drawer is open
	const [drawerSaving, setDrawerSaving] = useState(false);

	// Fetch History Audit Log
	const loadHistory = useCallback(async () => {
		setLoadingHistory(true);
		try {
			const data = await getNotificationHistory({ limit: 50 });
			if (Array.isArray(data)) {
				setHistoryLogs(data);
			}
		} catch (err) {
			console.error('Failed to load notification history:', err);
		} finally {
			setLoadingHistory(false);
		}
	}, []);

	const loadSnoozed = useCallback(async () => {
		setLoadingSnoozed(true);
		try {
			const data = await getSnoozedBatches();
			setSnoozedBatches(Array.isArray(data) ? data : []);
		} catch (err) {
			toast.error('Failed to load snoozed alerts');
		} finally {
			setLoadingSnoozed(false);
		}
	}, []);

	const handleUnsnooze = async (item) => {
		setUnsnoozingId(item.product_batch_id);
		const res = await unsnoozeBatch(item.product_batch_id);
		setUnsnoozingId(null);
		if (res?.success) {
			toast.success(`Alerts resumed for ${item.product_name} (batch ${item.batch_no})`);
			setSnoozedBatches((list) => list.filter((b) => b.product_batch_id !== item.product_batch_id));
		} else {
			toast.error(res?.message || 'Failed to resume alerts');
		}
	};

	// Fetch Preferences on Mount
	useEffect(() => {
		loadPreferences();
		loadHistory();
	}, [loadHistory]);

	// Refresh History whenever History tab is activated
	useEffect(() => {
		if (activeTab === 'history') {
			loadHistory();
		}
	}, [activeTab, loadHistory]);

	// Refresh the Snoozed Alerts list whenever its tab is activated
	useEffect(() => {
		if (activeTab === 'snoozed') {
			loadSnoozed();
		}
	}, [activeTab, loadSnoozed]);

	const loadPreferences = async () => {
		setLoading(true);
		try {
			const res = await getNotificationPreferences();
			if (res) {
				// Rules and schedules from backend are kept in state; unimplemented items are rendered disabled.
				if (res.rules) setRules(res.rules);
				if (res.channels) setChannels(res.channels);
				if (res.role_matrix) setRoleMatrix(res.role_matrix);
				if (res.quiet_hours) setQuietHours(res.quiet_hours);
				if (res.schedules) setSchedules(res.schedules);
				if (res.timezone) setOrgTimezone(res.timezone);
				if (res.available_roles && Array.isArray(res.available_roles) && res.available_roles.length > 0) {
					setAvailableRoles(res.available_roles);
				}
				setHasChanges(false);
			}
		} catch (err) {
			console.error('Failed to load notification preferences:', err);
			toast.error('Failed to load notification preferences');
		} finally {
			setLoading(false);
		}
	};

	// Trigger Manual Test Alert
	const handleTestTrigger = async (ruleId) => {
		const isDrawerRule =
			drawerRule &&
			(drawerRule.id === ruleId ||
				`${drawerRule.id}_job` === ruleId ||
				drawerRule.id.replace(/_job$/, '') === ruleId);

		const targetRule = isDrawerRule
			? drawerRule
			: (rules.find(
					(r) =>
						r.id === ruleId ||
						r.id === `${ruleId}_job` ||
						r.id.replace(/_job$/, '') === ruleId,
			  ) || drawerRule);
		if (!targetRule) return;
		if (targetRule.implemented === false) {
			toast.info('This feature is currently under development on our roadmap and cannot be tested yet.');
			return;
		}

		// The test runs against the SAVED settings (plus this drawer's values, when opened from it).
		if (hasChanges && !isDrawerRule) {
			toast.info('You have unsaved changes. Save them first — a test alert uses your saved settings.');
			return;
		}

		setTestingRuleId(targetRule.id);
		try {
			const res = await triggerTestNotification({
				rule_id: targetRule.id,
				// Test exactly what the rule delivers: 'both' tests in-app AND email.
				channel: targetRule.channel || 'in_app',
				...(isDrawerRule ? { rule_override: drawerRule } : {}),
			});

			const results = Array.isArray(res?.results) ? res.results : [];
			if (results.length) {
				// One line per channel, so a working in-app test can't hide a failed email test.
				const label = { in_app: 'In-app', email: 'Email' };
				const sentOk = results.filter((r) => r.status === 'sent');
				const notSent = results.filter((r) => r.status !== 'sent');
				const okText = sentOk
					.map((r) =>
						r.channel === 'email' ? r.reason : 'In-app notification sent — check the bell icon',
					)
					.join(' · ');
				const badText = notSent
					.map((r) => `${label[r.channel] || r.channel} not sent — ${r.reason}`)
					.join(' · ');
				if (!notSent.length) toast.success(`Test alert for '${targetRule.title}': ${okText}`);
				else if (sentOk.length) toast.warn(`${okText}. ${badText}`);
				else toast.warn(badText);
			} else if (res?.sent) {
				toast.success(res?.message || `Test alert for '${targetRule.title}' sent successfully! Check bell icon.`);
			} else {
				toast.warn(res?.reason || `Alert suppressed for '${targetRule.title}' based on preferences.`);
			}
			// Automatically refresh history to display the new trigger
			loadHistory();
		} catch (err) {
			console.error('Failed to trigger test notification:', err);
			toast.error(err?.message || 'Failed to trigger test notification');
		} finally {
			setTestingRuleId(null);
		}
	};

	// Format timestamp helper
	const formatTime = (isoString) => {
		if (!isoString) return 'Just now';
		try {
			const d = new Date(isoString);
			if (isNaN(d.getTime())) return String(isoString);
			return d.toLocaleString('en-US', {
				month: 'short',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
				hour12: true,
				...(orgTimezone ? { timeZone: orgTimezone } : {}),
			});
		} catch (e) {
			return String(isoString);
		}
	};

	// Save Preferences
	const handleSave = async () => {
		if (!canEdit) {
			toast.error('You do not have permission to modify notification preferences');
			return;
		}

		setSaving(true);
		try {
			const payload = {
				channels,
				role_matrix: roleMatrix,
				rules,
				quiet_hours: quietHours,
			};

			const res = await updateNotificationPreferences(payload);
			if (
				res?.success ||
				res?.status === 200 ||
				res?.message?.toLowerCase().includes('success') ||
				res?.rules
			) {
				toast.success(res?.message || 'Notification preferences saved successfully');
				setHasChanges(false);
				if (res?.rules) setRules(res.rules);
				if (res?.schedules) setSchedules(res.schedules);
				if (res?.quiet_hours) setQuietHours(res.quiet_hours);
				if (res?.channels) setChannels(res.channels);
				if (res?.role_matrix) setRoleMatrix(res.role_matrix);
			} else {
				toast.error(res?.message || 'Failed to save notification preferences');
			}
		} catch (err) {
			console.error('Error saving notification preferences:', err);
			toast.error('An error occurred while saving preferences');
		} finally {
			setSaving(false);
		}
	};

	// Reset to Defaults
	const handleReset = async () => {
		if (!canEdit) {
			toast.error('You do not have permission to modify notification preferences');
			return;
		}

		if (window.confirm('Are you sure you want to reset all notification preferences to system defaults?')) {
			setSaving(true);
			try {
				await updateNotificationPreferences({
					reset: true,
				});
				toast.info('Preferences reset to default values');
				await loadPreferences();
			} catch (err) {
				toast.error('Failed to reset preferences');
			} finally {
				setSaving(false);
			}
		}
	};

	// Module filter: only modules that have a rule on this page.
	const moduleOptions = useMemo(
		() => [...new Set(rules.map((r) => r.module).filter(Boolean))],
		[rules],
	);

	// An alert that belongs to a scheduled job (Schedules tab) is only sent while that job is on.
	// `pausedJobFor(rule)` returns the job when the alert itself is enabled but its job is off.
	const jobById = useMemo(() => new Map(rules.map((r) => [r.id, r])), [rules]);
	const pausedJobFor = (rule) => {
		if (!rule?.enabled || !rule.scheduleManagedBy) return null;
		const job = jobById.get(rule.scheduleManagedBy);
		return job && !job.enabled ? job : null;
	};

	// Module grouping
	const groupedRules = useMemo(() => {
		const groups = {};
		rules.forEach((rule) => {
			const mod = rule.module || 'Other';
			if (!groups[mod]) groups[mod] = [];

			// Apply search filter
			const matchesSearch =
				!searchTerm ||
				rule.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
				rule.description.toLowerCase().includes(searchTerm.toLowerCase());

			// Apply module filter
			const matchesModule =
				selectedModule === 'all' ||
				mod === selectedModule ||
				mod.toLowerCase().startsWith(selectedModule.toLowerCase());

			// Apply status filter
			const matchesStatus =
				statusFilter === 'all' ||
				(statusFilter === 'enabled' && rule.enabled) ||
				(statusFilter === 'disabled' && !rule.enabled);

			if (matchesSearch && matchesModule && matchesStatus) {
				groups[mod].push(rule);
			}
		});
		return groups;
	}, [rules, searchTerm, selectedModule, statusFilter]);

	// Calculate active permitted roles based on rule.defaultRoles AND Role Permission Matrix
	const getEffectiveRolesForRule = useCallback(
		(rule) => {
			const availableMap = new Map(
				availableRoles.map((r) => [String(r).toLowerCase().trim(), r]),
			);

			const rawRoles = (
				rule.defaultRoles && rule.defaultRoles.length > 0
					? rule.defaultRoles
					: availableRoles
			).map((r) => {
				const lower = String(r).toLowerCase().trim();
				if (lower.includes('owner')) return 'Organisation Owner';
				if (lower.includes('manager') || lower.includes('admin')) return 'Manager';
				if (lower.includes('accountant')) return 'Accountant';
				if (lower.includes('staff') || lower.includes('pharmacist')) return 'Staff';
				return r;
			});

			const mod = (rule.module || '').toLowerCase();

			// Filter target roles by available roles & the active Role Permission Matrix
			const permittedRoles = rawRoles
				.map((r) => {
					const lower = String(r).toLowerCase().trim();
					return availableMap.get(lower) || r;
				})
				.filter((roleName) => {
					if (availableRoles && availableRoles.length > 0) {
						const lower = String(roleName).toLowerCase().trim();
						if (!availableMap.has(lower)) return false;
					}

					const perms = roleMatrix[roleName];
					if (!perms) return true; // If role not configured in matrix, allow by default

					if (mod.includes('inventory') && perms.inventory === false) return false;
					if (mod.includes('sales') && perms.sales === false) return false;
					if (mod.includes('purchase') && perms.purchase === false) return false;
					if ((mod.includes('email') || rule.channel === 'email') && perms.email === false) return false;

					return true;
				});

			return [...new Set(permittedRoles)];
		},
		[availableRoles, roleMatrix],
	);

	// Why an alert that is switched ON would still not be sent (or only half sent).
	// kind: 'job' (its schedule is off) | 'channel' (the organisation-wide channel switch is off)
	//       | 'roles' (nobody may receive it) | 'partial' (one of its two channels is off)
	//       | 'unimplemented' (feature under development on product roadmap).
	const blockInfo = (rule) => {
		if (rule?.implemented === false) {
			return {
				kind: 'unimplemented',
				badge: 'Not Implemented Yet',
				text: 'This notification routine is currently under development on our roadmap and cannot be enabled yet.',
			};
		}

		if (!rule?.enabled) return null;

		const job = pausedJobFor(rule);
		if (job) {
			return {
				kind: 'job',
				job,
				badge: 'Paused — schedule off',
				text: `Won't send — “${job.title}” schedule is off.`,
			};
		}

		const wantsInApp = rule.channel !== 'email';
		const wantsEmail = rule.channel !== 'in_app';
		const inAppOn = channels.in_app !== false;
		const emailOn = channels.email !== false;
		if (!((wantsInApp && inAppOn) || (wantsEmail && emailOn))) {
			const label =
				rule.channel === 'email' ? 'Email' : rule.channel === 'in_app' ? 'In-app' : 'In-app and email';
			return {
				kind: 'channel',
				badge: 'Paused — channel off',
				text: `Enabled, but ${label} notifications are switched off for the whole organisation (Channels & Permissions).`,
			};
		}

		if (getEffectiveRolesForRule(rule).length === 0) {
			return {
				kind: 'roles',
				badge: 'No recipients',
				text: 'Enabled, but no role is currently allowed to receive it (see the role permissions in Channels & Permissions).',
			};
		}

		if (wantsInApp && wantsEmail && inAppOn !== emailOn) {
			return {
				kind: 'partial',
				badge: null,
				text: `${emailOn ? 'In-app' : 'Email'} notifications are switched off for the whole organisation, so this alert is delivered by ${emailOn ? 'email' : 'in-app'} only.`,
			};
		}
		return null;
	};

	// Stats Calculation
	const implementedRules = rules.filter((r) => r.implemented !== false);
	const notSendingCount = implementedRules.filter((r) => {
		const b = blockInfo(r);
		return b && b.kind !== 'partial' && b.kind !== 'unimplemented';
	}).length;
	const stats = {
		activeRules: `${implementedRules.filter((r) => r.enabled).length - notSendingCount} / ${implementedRules.length}`,
		pausedCount: notSendingCount,
		digestGroups: implementedRules.filter((r) => r.digestMode).length,
		criticalAlerts: implementedRules.filter((r) => r.severity === 'critical' || r.priority === 'critical')
			.length,
		cooldownDefault: quietHours?.enabled ? `${quietHours.start} - ${quietHours.end}` : '24h default',
	};

	// Toggle Rule Enabled State
	const handleToggleRule = async (ruleId) => {
		if (!canEdit) return;
		const targetRule = rules.find((r) => r.id === ruleId);
		if (targetRule?.implemented === false) {
			toast.info('This feature is currently under development on our roadmap and cannot be enabled yet.');
			return;
		}
		const nextEnabled = targetRule ? !targetRule.enabled : true;

		const updatedRules = rules.map((r) =>
			r.id === ruleId ? { ...r, enabled: nextEnabled } : r,
		);
		const updatedSchedules = schedules.map((s) => {
			const isMatch =
				s.rule_id === ruleId ||
				s.id === ruleId ||
				`${s.id}_job` === ruleId ||
				s.id === ruleId.replace(/_job$/, '');
			if (!isMatch) return s;
			return {
				...s,
				status: nextEnabled ? 'Enabled' : 'Disabled',
			};
		});

		// Optimistically update UI
		setRules(updatedRules);
		setSchedules(updatedSchedules);

		// Persist directly to backend database
		try {
			const payload = {
				channels,
				role_matrix: roleMatrix,
				rules: updatedRules,
				quiet_hours: quietHours,
			};
			const res = await updateNotificationPreferences(payload);
			if (
				res?.success ||
				res?.status === 200 ||
				res?.message?.toLowerCase().includes('success') ||
				res?.rules
			) {
				toast.success(
					nextEnabled
						? `'${targetRule?.title || ruleId}' enabled and saved`
						: `'${targetRule?.title || ruleId}' disabled and saved`,
				);
				setHasChanges(false);
				if (res?.rules) setRules(res.rules);
				if (res?.schedules) setSchedules(res.schedules);
				if (res?.quiet_hours) setQuietHours(res.quiet_hours);
				if (res?.channels) setChannels(res.channels);
				if (res?.role_matrix) setRoleMatrix(res.role_matrix);
			} else {
				toast.info(`Alert ${nextEnabled ? 'enabled' : 'disabled'} locally. Click Save Changes to apply.`);
				setHasChanges(true);
			}
		} catch (err) {
			console.error('Failed to auto-save rule toggle:', err);
			toast.info(`Alert ${nextEnabled ? 'enabled' : 'disabled'} locally. Click Save Changes to apply.`);
			setHasChanges(true);
		}
	};

	// Toggle Schedule Enabled State
	const handleToggleSchedule = async (job) => {
		if (!canEdit) return;
		const nextEnabled = job.status !== 'Enabled';
		const nextStatus = nextEnabled ? 'Enabled' : 'Disabled';
		const ruleId = job.rule_id || `${job.id}_job`;

		const updatedSchedules = schedules.map((s) =>
			s.id === job.id || s.rule_id === ruleId
				? { ...s, status: nextStatus }
				: s,
		);

		let foundJobRule = false;
		const updatedRules = rules.map((r) => {
			const isJobMatch =
				r.id === ruleId ||
				r.id === job.id ||
				r.id === `${job.id}_job` ||
				r.id.replace(/_job$/, '') === job.id;
			if (isJobMatch) {
				foundJobRule = true;
				return { ...r, enabled: nextEnabled };
			}
			return r;
		});

		if (!foundJobRule) {
			updatedRules.push({
				id: ruleId,
				title: job.name,
				module: 'Scheduled Jobs',
				enabled: nextEnabled,
			});
		}

		// Optimistically update UI
		setSchedules(updatedSchedules);
		setRules(updatedRules);

		// Persist directly to backend database
		try {
			const payload = {
				channels,
				role_matrix: roleMatrix,
				rules: updatedRules,
				quiet_hours: quietHours,
			};

			const res = await updateNotificationPreferences(payload);
			if (
				res?.success ||
				res?.status === 200 ||
				res?.message?.toLowerCase().includes('success') ||
				res?.rules
			) {
				toast.success(
					nextEnabled
						? `'${job.name}' schedule enabled and saved`
						: `'${job.name}' schedule disabled and saved — alerts under it are paused`,
				);
				setHasChanges(false);
				if (res?.rules) setRules(res.rules);
				if (res?.schedules) setSchedules(res.schedules);
				if (res?.quiet_hours) setQuietHours(res.quiet_hours);
			} else {
				toast.info(`Schedule ${nextStatus.toLowerCase()} locally. Click Save Changes to apply.`);
				setHasChanges(true);
			}
		} catch (err) {
			console.error('Failed to auto-save schedule toggle:', err);
			toast.info(`Schedule ${nextStatus.toLowerCase()} locally. Click Save Changes to apply.`);
			setHasChanges(true);
		}
	};

	// Toggle Quiet Hours Enabled State
	const handleToggleQuietHours = async () => {
		if (!canEdit) return;
		const nextEnabled = !quietHours.enabled;
		const nextQuiet = { ...quietHours, enabled: nextEnabled };
		setQuietHours(nextQuiet);

		try {
			const payload = {
				channels,
				role_matrix: roleMatrix,
				rules,
				quiet_hours: nextQuiet,
			};
			const res = await updateNotificationPreferences(payload);
			if (
				res?.success ||
				res?.status === 200 ||
				res?.message?.toLowerCase().includes('success') ||
				res?.quiet_hours
			) {
				toast.success(
					nextEnabled
						? 'Quiet hours enabled and saved'
						: 'Quiet hours disabled and saved',
				);
				setHasChanges(false);
				if (res?.rules) setRules(res.rules);
				if (res?.schedules) setSchedules(res.schedules);
				if (res?.quiet_hours) setQuietHours(res.quiet_hours);
			} else {
				toast.info(`Quiet hours ${nextEnabled ? 'enabled' : 'disabled'} locally. Click Save Changes to apply.`);
				setHasChanges(true);
			}
		} catch (err) {
			console.error('Failed to auto-save quiet hours toggle:', err);
			toast.info(`Quiet hours ${nextEnabled ? 'enabled' : 'disabled'} locally. Click Save Changes to apply.`);
			setHasChanges(true);
		}
	};

	// Switch a schedule job on from the alert that depends on it.
	const handleEnableJob = async (jobRuleId) => {
		if (!canEdit) return;
		const job = schedules.find((s) => s.rule_id === jobRuleId);
		if (job) {
			if (job.status !== 'Enabled') await handleToggleSchedule(job);
		} else {
			const updatedRules = rules.map((r) => (r.id === jobRuleId ? { ...r, enabled: true } : r));
			setRules(updatedRules);
			try {
				const res = await updateNotificationPreferences({
					channels,
					role_matrix: roleMatrix,
					rules: updatedRules,
					quiet_hours: quietHours,
				});
				if (res?.rules) setRules(res.rules);
				if (res?.schedules) setSchedules(res.schedules);
				toast.success('Schedule enabled and saved');
				setHasChanges(false);
			} catch (err) {
				setHasChanges(true);
				toast.info('Schedule turned on — click Save Changes to apply it.');
			}
		}
	};

	// Open Drawer to Configure Schedule
	const handleConfigureSchedule = (job) => {
		const matchingRule = rules.find(
			(r) =>
				r.id === job.rule_id ||
				r.id === job.id ||
				r.id === `${job.id}_job` ||
				r.id.replace(/_job$/, '') === job.id,
		);

		if (matchingRule) {
			setDrawerRule({
				...matchingRule,
				triggerTime: normalizeTimeValue(matchingRule.triggerTime || job.trigger_time),
				frequency: matchingRule.frequency?.toLowerCase() || job.frequency?.toLowerCase() || 'daily',
				implemented: matchingRule.implemented !== false && job.implemented !== false,
			});
		} else {
			setDrawerRule({
				id: job.rule_id || `${job.id}_job`,
				title: job.name,
				module: 'Scheduled Jobs',
				enabled: job.status === 'Enabled',
				implemented: job.implemented !== false,
				channel: job.channel || 'both',
				frequency: job.frequency?.toLowerCase() || 'daily',
				triggerTime: normalizeTimeValue(job.trigger_time),
				triggerType: 'scheduled',
				digestMode: (job.advanced || '').includes('digest'),
			});
		}
	};

	// Change Rule Channel
	const handleChangeChannel = async (ruleId, channel) => {
		if (!canEdit) return;
		const targetRule = rules.find((r) => r.id === ruleId);
		if (targetRule?.implemented === false) return;
		const updatedRules = rules.map((r) =>
			r.id === ruleId ? { ...r, channel } : r,
		);
		setRules(updatedRules);

		try {
			const payload = {
				channels,
				role_matrix: roleMatrix,
				rules: updatedRules,
				quiet_hours: quietHours,
			};
			const res = await updateNotificationPreferences(payload);
			if (
				res?.success ||
				res?.status === 200 ||
				res?.message?.toLowerCase().includes('success') ||
				res?.rules
			) {
				const channelLabel =
					{ in_app: 'In-app only', email: 'Email only', both: 'In-app & Email' }[channel] || channel;
				toast.success(
					`'${targetRule?.title || ruleId}' channel updated to ${channelLabel} and saved`,
				);
				setHasChanges(false);
				if (res?.rules) setRules(res.rules);
				if (res?.schedules) setSchedules(res.schedules);
				if (res?.quiet_hours) setQuietHours(res.quiet_hours);
				if (res?.channels) setChannels(res.channels);
				if (res?.role_matrix) setRoleMatrix(res.role_matrix);
			} else {
				setHasChanges(true);
			}
		} catch (err) {
			console.error('Failed to auto-save channel change:', err);
			setHasChanges(true);
		}
	};

	// Toggle Master Channel
	const handleToggleChannel = async (channelKey) => {
		if (!canEdit) return;
		const nextEnabled = !channels[channelKey];
		const nextChannels = {
			...channels,
			[channelKey]: nextEnabled,
		};
		setChannels(nextChannels);

		try {
			const payload = {
				channels: nextChannels,
				role_matrix: roleMatrix,
				rules,
				quiet_hours: quietHours,
			};
			const res = await updateNotificationPreferences(payload);
			if (
				res?.success ||
				res?.status === 200 ||
				res?.message?.toLowerCase().includes('success') ||
				res?.channels
			) {
				const channelLabel = {
					in_app: 'In-app notifications',
					email: 'Email master switch',
					customer_email: 'Customer emails',
					vendor_email: 'Vendor emails',
					org_user_email: 'Team member emails',
				}[channelKey] || channelKey;

				toast.success(
					nextEnabled
						? `${channelLabel} enabled and saved`
						: `${channelLabel} disabled and saved`,
				);
				setHasChanges(false);
				if (res?.rules) setRules(res.rules);
				if (res?.schedules) setSchedules(res.schedules);
				if (res?.quiet_hours) setQuietHours(res.quiet_hours);
				if (res?.channels) setChannels(res.channels);
				if (res?.role_matrix) setRoleMatrix(res.role_matrix);
			} else {
				setHasChanges(true);
			}
		} catch (err) {
			console.error('Failed to auto-save channel toggle:', err);
			setHasChanges(true);
		}
	};

	// Toggle Role Matrix Checkbox
	const handleToggleRolePermission = async (role, category) => {
		if (!canEdit) return;
		const currentRole = roleMatrix[role] || {};
		const isNowEnabled = !currentRole[category];
		const nextRoleMatrix = {
			...roleMatrix,
			[role]: {
				...currentRole,
				[category]: isNowEnabled,
			},
		};
		setRoleMatrix(nextRoleMatrix);

		try {
			const payload = {
				channels,
				role_matrix: nextRoleMatrix,
				rules,
				quiet_hours: quietHours,
			};
			const res = await updateNotificationPreferences(payload);
			if (
				res?.success ||
				res?.status === 200 ||
				res?.message?.toLowerCase().includes('success') ||
				res?.role_matrix
			) {
				const catLabel = category.charAt(0).toUpperCase() + category.slice(1);
				toast.success(
					`${role} ${catLabel} alert permission ${isNowEnabled ? 'enabled' : 'disabled'} and saved`,
				);
				setHasChanges(false);
				if (res?.rules) setRules(res.rules);
				if (res?.schedules) setSchedules(res.schedules);
				if (res?.quiet_hours) setQuietHours(res.quiet_hours);
				if (res?.channels) setChannels(res.channels);
				if (res?.role_matrix) setRoleMatrix(res.role_matrix);
			} else {
				setHasChanges(true);
			}
		} catch (err) {
			console.error('Failed to auto-save role matrix toggle:', err);
			setHasChanges(true);
		}
	};

	// Drawer updates
	const handleUpdateDrawerRule = (updatedFields) => {
		if (!canEdit) return;
		setDrawerRule((prev) => {
			const next = {
				...prev,
				...updatedFields,
			};
			if (
				updatedFields.triggerType ||
				updatedFields.triggerTime ||
				updatedFields.frequency ||
				updatedFields.weekday !== undefined ||
				updatedFields.leadDays !== undefined
			) {
				const timeStr = normalizeTimeValue(next.triggerTime);
				const freq = (next.frequency || 'daily').toLowerCase();
				next.triggerTime = timeStr;
				next.frequency = freq;
				next.scheduleText = getDisplayScheduleText(next);
			}

			// Ensure cooldown is correlated with trigger schedule and frequency
			const allowedCooldowns = getCorrelatedCooldownOptions(next);
			const currentCooldown = (next.cooldown || 'none').toLowerCase();
			const isAllowed = allowedCooldowns.some((c) => c.value === currentCooldown);
			if (!isAllowed) {
				const fallbackCooldown =
					allowedCooldowns[allowedCooldowns.length - 1]?.value || 'none';
				next.cooldown = fallbackCooldown;
			}

			return next;
		});
	};

	const handleApplyDrawerRule = async () => {
		if (!drawerRule || !canEdit) return;
		if (drawerRule.implemented === false) {
			toast.info('This routine is on our roadmap and cannot be modified yet.');
			return;
		}

		// 1. Prepare updated rules
		const ruleExists = rules.some(
			(r) =>
				r.id === drawerRule.id ||
				r.id === `${drawerRule.id}_job` ||
				r.id.replace(/_job$/, '') === drawerRule.id,
		);

		let updatedRules;
		if (ruleExists) {
			updatedRules = rules.map((r) => {
				const isMatch =
					r.id === drawerRule.id ||
					r.id === `${drawerRule.id}_job` ||
					r.id.replace(/_job$/, '') === drawerRule.id;
				return isMatch
					? {
							...r,
							...drawerRule,
							id: r.id,
							scheduleText: getDisplayScheduleText({ ...r, ...drawerRule }),
						}
					: r;
			});
		} else {
			updatedRules = [
				...rules,
				{
					...drawerRule,
					scheduleText: getDisplayScheduleText(drawerRule),
				},
			];
		}

		// 2. Prepare updated schedules
		const updatedSchedules = schedules.map((s) => {
			const isMatch =
				s.rule_id === drawerRule.id ||
				s.id === drawerRule.id ||
				`${s.id}_job` === drawerRule.id ||
				s.id === drawerRule.id.replace(/_job$/, '');
			if (!isMatch) return s;
			const freq = drawerRule.frequency
				? drawerRule.frequency.charAt(0).toUpperCase() + drawerRule.frequency.slice(1)
				: s.frequency;
			const trigTime = drawerRule.triggerTime || s.trigger_time;
			const advanced = quietHours?.enabled
				? `${quietHours.start} - ${quietHours.end}${drawerRule.digestMode ? ', digest' : ''}`
				: drawerRule.digestMode
				? 'Digest enabled'
				: 'Standard';
			return {
				...s,
				status: drawerRule.enabled ? 'Enabled' : 'Disabled',
				frequency: freq,
				trigger_time: trigTime,
				advanced,
				channel: drawerRule.channel || s.channel,
			};
		});

		// Update UI immediately
		setRules(updatedRules);
		setSchedules(updatedSchedules);
		setDrawerSaving(true);

		// Persist directly to database
		try {
			const payload = {
				channels,
				role_matrix: roleMatrix,
				rules: updatedRules,
				quiet_hours: quietHours,
			};

			const res = await updateNotificationPreferences(payload);
			if (
				res?.success ||
				res?.status === 200 ||
				res?.message?.toLowerCase().includes('success') ||
				res?.rules
			) {
				toast.success(`Settings for '${drawerRule.title}' applied and saved successfully`);
				setHasChanges(false);
				if (res?.rules) setRules(res.rules);
				if (res?.schedules) setSchedules(res.schedules);
				if (res?.quiet_hours) setQuietHours(res.quiet_hours);
				if (res?.channels) setChannels(res.channels);
				if (res?.role_matrix) setRoleMatrix(res.role_matrix);
			} else {
				toast.info(`Changes applied locally for '${drawerRule.title}'. Remember to save changes.`);
				setHasChanges(true);
			}
		} catch (err) {
			console.error('Failed to save drawer preferences:', err);
			toast.info(`Changes applied locally for '${drawerRule.title}'. Remember to save changes.`);
			setHasChanges(true);
		} finally {
			setDrawerSaving(false);
			setDrawerRule(null);
		}
	};

	const toggleCollapse = (mod) => {
		setCollapsedModules((prev) => ({
			...prev,
			[mod]: !prev[mod],
		}));
	};

	const handleExpandAll = () => {
		setCollapsedModules({});
	};

	const handleCollapseAll = () => {
		const collapsed = {};
		Object.keys(groupedRules).forEach((k) => {
			collapsed[k] = true;
		});
		setCollapsedModules(collapsed);
	};

	if (loading) {
		return (
			<div className="notification-settings-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Loading...</span>
				</div>
			</div>
		);
	}

	return (
		<div className="notification-settings-page">
			{/* Sticky Top Header Block (Header + Tabs Bar) */}
			<div className="ns-sticky-header">
				<SettingHeaderComponent
					title="Notifications"
					subtitle="Configure event-driven and scheduled alert delivery across your organisation. Control in-app banners, email digests, role permissions, and quiet hours."
					buttons={[
						// Hidden: this header button only ever tested the first rule in the list.
						// Every rule has its own "Test Alert" button, which is the real test.
						// {
						// 	button: testingRuleId ? 'Sending Test...' : 'Send Test Alert',
						// 	className: 'btn-outline-primary',
						// 	handler: () => handleTestTrigger(rules[0]?.id || 'low_stock'),
						// 	disabled: testingRuleId !== null,
						// },
						canEdit && {
							button: 'Reset Defaults',
							className: 'btn-outline-secondary',
							handler: handleReset,
							disabled: saving,
						},
						canEdit && {
							button: saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved',
							className: 'btn-primary',
							handler: handleSave,
							disabled: saving || !hasChanges,
							loader: saving,
						},
					].filter(Boolean)}
				/>

				{/* Settings Navigation Tab Bar */}
				<div className="py-2 ns-tabs-bar">
					<ul className="nav mt-2">
						{[
							{ key: 'rules', label: 'Notification Preferences', icon: <SlidersHorizontal size={14} className="me-2" /> },
							{ key: 'channels', label: 'Channels & Permissions', icon: <ShieldCheck size={14} className="me-2" /> },
							{ key: 'schedules', label: 'Schedules', icon: <Clock size={14} className="me-2" /> },
							{ key: 'snoozed', label: 'Snoozed Alerts', icon: <BellOff size={14} className="me-2" /> },
							{ key: 'history', label: 'History', icon: <History size={14} className="me-2" /> },
						].map((tab) => (
							<li key={tab.key} className="nav-item">
								<div
									className={`tab-link ${activeTab === tab.key ? 'active' : ''} d-flex align-items-center cursor-pointer`}
									onClick={() => setActiveTab(tab.key)}
								>
									{tab.icon}
									{tab.label}
								</div>
							</li>
						))}
					</ul>
				</div>
			</div>

			{/* Main Settings Body */}
			<div className="notification-settings-wrapper">
				{/* Read-Only Mode Banner if user is not org owner or lack settings permission */}
				{!canEdit && (
					<div className="ns-readonly-banner shadow-sm">
						<AlertCircle size={18} className="banner-icon" />
						<span>
							<strong>Read-Only Mode:</strong> You can review organization notification preferences. Only the Organisation Owner and authorized administrators can modify alert rules, channels, and quiet hours.
						</span>
					</div>
				)}

				{/* Stats Grid */}
				<div className="ns-stats-grid">
					<div className="ns-stat-card">
						<span>Active Rules</span>
						<strong>{stats.activeRules}</strong>
						<small>
							{stats.pausedCount > 0
								? `${stats.pausedCount} enabled but not sending — see rows marked Paused`
								: 'Enabled alert conditions'}
						</small>
					</div>
					<div className="ns-stat-card">
						<span>Digest Groups</span>
						<strong>{stats.digestGroups}</strong>
						<small>Batched summaries active</small>
					</div>
					<div className="ns-stat-card">
						<span>Critical Alerts</span>
						<strong>{stats.criticalAlerts}</strong>
						<small>High priority notifications</small>
					</div>
					<div className="ns-stat-card">
						<span>Quiet Hours</span>
						<strong>{quietHours?.enabled ? 'Active' : 'Disabled'}</strong>
						<small>{stats.cooldownDefault}</small>
					</div>
				</div>

				{/* TAB 1: NOTIFICATION PREFERENCES (RULES) */}
				{activeTab === 'rules' && (
					<div className="ns-rules-view">
						{/* Search & Filters */}
						<div className="ns-filter-bar">
							<div className="ns-search-box">
								<Search size={16} className="search-icon" />
								<input
									type="text"
									placeholder="Search notification rules..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
								/>
							</div>

							<div className="ns-filter-controls">
								<select
									value={selectedModule}
									onChange={(e) => setSelectedModule(e.target.value)}
								>
									<option value="all">All Modules</option>
									{moduleOptions.map((m) => (
										<option key={m} value={m}>
											{m}
										</option>
									))}
								</select>

								<div className="ns-filter-pills">
									<button
										className={statusFilter === 'all' ? 'active' : ''}
										onClick={() => setStatusFilter('all')}
									>
										All
									</button>
									<button
										className={statusFilter === 'enabled' ? 'active' : ''}
										onClick={() => setStatusFilter('enabled')}
									>
										Enabled
									</button>
									<button
										className={statusFilter === 'disabled' ? 'active' : ''}
										onClick={() => setStatusFilter('disabled')}
									>
										Disabled
									</button>
								</div>

								<button
									className="ns-btn-text"
									onClick={handleExpandAll}
								>
									Expand All
								</button>
								<button
									className="ns-btn-text"
									onClick={handleCollapseAll}
								>
									Collapse All
								</button>
							</div>
						</div>

						{/* Rules Accordions by Module */}
						{Object.keys(groupedRules).length === 0 ? (
							<div className="ns-empty-state">
								No notification rules match your filters.
							</div>
						) : (
							Object.entries(groupedRules).map(([moduleName, moduleRules]) => {
								if (moduleRules.length === 0) return null;
								const isCollapsed = collapsedModules[moduleName];

								return (
									<div key={moduleName} className="ns-module-section">
										<div
											className="ns-module-header"
											onClick={() => toggleCollapse(moduleName)}
										>
											<div className="ns-module-title">
												<span>{moduleName}</span>
												<span className="ns-rule-badge">
													{moduleRules.length} rules
												</span>
											</div>
											<div className="ns-toggle-chevron">
												{isCollapsed ? (
													<ChevronDown size={18} />
												) : (
													<ChevronUp size={18} />
												)}
											</div>
										</div>

										{!isCollapsed && (
											<div className="ns-rules-list">
												{moduleRules.map((rule) => {
													const block = blockInfo(rule);
													const stopped = !!block && block.kind !== 'partial';
													const testBlocked =
														!!block &&
														(block.kind === 'job' ||
															block.kind === 'channel' ||
															block.kind === 'unimplemented');
													return (
													<div key={rule.id} className={`ns-rule-card${stopped ? ' is-paused' : ''}`}>
														<div className="ns-rule-info">
															<div className="ns-rule-top">
																<h3>{rule.title}</h3>
																<span
																	className={`badge-severity ${
																		rule.severity || rule.priority || 'info'
																	}`}
																>
																	{rule.severity || rule.priority || 'info'}
																</span>
																{block?.badge && (
																	<span
																		className={
																			block.kind === 'unimplemented'
																				? 'badge bg-secondary text-white ms-1'
																				: 'ns-paused-badge'
																		}
																		style={
																			block.kind === 'unimplemented'
																				? { fontSize: '10px', padding: '3px 7px', borderRadius: '4px' }
																				: {}
																		}
																	>
																		{block.badge}
																	</span>
																)}
															</div>
															<p>{rule.description}</p>
															{rules.some((r) => r.scheduleManagedBy === rule.id) && (
																<div className="ns-job-controls mb-1">
																	Controls:{' '}
																	{rules
																		.filter((r) => r.scheduleManagedBy === rule.id)
																		.map((r) => r.title)
																		.join(', ')}
																</div>
															)}
															{(() => {
																const effectiveRoles = getEffectiveRolesForRule(rule);
																return (
																	<div className="ns-rule-meta">
																		<span>
																			<Clock size={13} />
																			{getDisplayScheduleText(rule) || rule.scheduleText || (rule.triggerType === 'scheduled' ? `Daily at ${rule.triggerTime}` : 'Instant on event')}
																		</span>
																		{rule.cooldown && rule.cooldown !== 'none' && (
																			<span>
																				<Timer size={13} />
																				Cooldown: {rule.cooldown}
																			</span>
																		)}
																		{rule.digestMode && (
																			<span>
																				<Layers size={13} />
																				Digest mode
																			</span>
																		)}
																		<span className="ns-meta-roles">
																			<Users size={13} />
																			{effectiveRoles.length > 0
																				? effectiveRoles.join(', ')
																				: 'No roles permitted'}
																		</span>
																	</div>
																);
															})()}
															{block && (
																<div className="ns-paused-note">
																	<span className="ns-paused-text">{block.text}</span>
																	{block.kind !== 'unimplemented' && (
																		<div className="ns-paused-actions">
																			{block.kind === 'job' && canEdit && (
																				<button
																					type="button"
																					className="ns-link-btn"
																					onClick={() => handleEnableJob(block.job.id)}
																				>
																					Turn on schedule
																				</button>
																			)}
																			<button
																				type="button"
																				className="ns-link-btn"
																				onClick={() => setActiveTab(block.kind === 'job' ? 'schedules' : 'channels')}
																			>
																				{block.kind === 'job' ? 'View schedules' : 'Open channels & permissions'}
																			</button>
																		</div>
																	)}
																</div>
															)}
														</div>

														<div className="ns-rule-actions">
															{/* Channel Pills */}
															<div className="ns-channel-pills">
																{!rule.lockedChannel && (
																	<button
																		className={rule.channel === 'in_app' ? 'active' : ''}
																		onClick={() => handleChangeChannel(rule.id, 'in_app')}
																		disabled={!canEdit || rule.implemented === false}
																	>
																		In-app
																	</button>
																)}
																{(!rule.lockedChannel || rule.lockedChannel === 'email') && (
																	<button
																		className={rule.channel === 'email' ? 'active' : ''}
																		onClick={() => handleChangeChannel(rule.id, 'email')}
																		disabled={!canEdit || !!rule.lockedChannel || rule.implemented === false}
																	>
																		Email
																	</button>
																)}
																{!rule.lockedChannel && (
																	<button
																		className={rule.channel === 'both' ? 'active' : ''}
																		onClick={() => handleChangeChannel(rule.id, 'both')}
																		disabled={!canEdit || rule.implemented === false}
																	>
																		Both
																	</button>
																)}
															</div>

															{/* Enable Toggle Switch */}
															<label className={`ns-toggle-switch ${!canEdit || rule.implemented === false ? 'disabled' : ''}`}>
																<input
																	type="checkbox"
																	checked={rule.implemented === false ? false : !!rule.enabled}
																	onChange={() => handleToggleRule(rule.id)}
																	disabled={!canEdit || rule.implemented === false}
																	title={rule.implemented === false ? 'This alert rule is under development on our roadmap' : ''}
																/>
																<span className="ns-slider"></span>
															</label>

															{/* Test Trigger Button */}
															{showTestAlert && (
																<button
																	className="ns-btn-test"
																	onClick={() => handleTestTrigger(rule.id)}
																	disabled={rule.implemented === false || testingRuleId === rule.id || testBlocked}
																	title={
																		rule.implemented === false
																			? 'This notification routine is not implemented yet'
																			: testBlocked
																				? block.kind === 'job'
																					? `Turn on the “${block.job.title}” schedule to send a test`
																					: 'Switch this channel on in Channels & Permissions to send a test'
																				: 'Send a live test alert for this notification'
																	}
																>
																	<Bell size={13} className="me-1" />
																	{testingRuleId === rule.id ? 'Sending...' : 'Test Alert'}
																</button>
															)}

															{/* Configure Button */}
															<button
																className="ns-btn-configure"
																onClick={() =>
																	setDrawerRule({
																		...rule,
																		triggerTime: normalizeTimeValue(rule.triggerTime),
																		frequency: (rule.frequency || 'daily').toLowerCase(),
																	})
																}
															>
																Configure
															</button>
														</div>
													</div>
													);
												})}
											</div>
										)}
									</div>
								);
							})
						)}
					</div>
				)}

				{/* TAB 2: CHANNELS & PERMISSIONS */}
				{activeTab === 'channels' && (
					<div className="ns-channels-view">
						{/* Master Channels */}
						<div className="ns-card">
							<h2>Delivery Channels</h2>
							<div className="ns-card-desc">
								Global master switches for outbound communication channels. Disabling a channel blocks delivery across all rules regardless of individual configurations.
							</div>

							<div className="ns-channel-switches-grid">
								<div className="ns-channel-row">
									<span>In-App Notifications</span>
									<label className={`ns-toggle-switch ${!canEdit ? 'disabled' : ''}`}>
										<input
											type="checkbox"
											checked={!!channels.in_app}
											onChange={() => handleToggleChannel('in_app')}
											disabled={!canEdit}
										/>
										<span className="ns-slider"></span>
									</label>
								</div>

								<div className="ns-channel-row">
									<span>Email Master Switch</span>
									<label className={`ns-toggle-switch ${!canEdit ? 'disabled' : ''}`}>
										<input
											type="checkbox"
											checked={!!channels.email}
											onChange={() => handleToggleChannel('email')}
											disabled={!canEdit}
										/>
										<span className="ns-slider"></span>
									</label>
								</div>

								<div className="ns-channel-row">
									<span>Customer Facing Emails</span>
									<label className={`ns-toggle-switch ${!canEdit ? 'disabled' : ''}`}>
										<input
											type="checkbox"
											checked={!!channels.customer_email}
											onChange={() => handleToggleChannel('customer_email')}
											disabled={!canEdit}
										/>
										<span className="ns-slider"></span>
									</label>
								</div>

								<div className="ns-channel-row">
									<span>Vendor Facing Emails</span>
									<label className={`ns-toggle-switch ${!canEdit ? 'disabled' : ''}`}>
										<input
											type="checkbox"
											checked={!!channels.vendor_email}
											onChange={() => handleToggleChannel('vendor_email')}
											disabled={!canEdit}
										/>
										<span className="ns-slider"></span>
									</label>
								</div>

								<div className="ns-channel-row">
									<span>Internal Org Emails</span>
									<label className={`ns-toggle-switch ${!canEdit ? 'disabled' : ''}`}>
										<input
											type="checkbox"
											checked={!!channels.org_user_email}
											onChange={() => handleToggleChannel('org_user_email')}
											disabled={!canEdit}
										/>
										<span className="ns-slider"></span>
									</label>
								</div>
							</div>
						</div>

						{/* Role Permission Matrix */}
						<div className="ns-card">
							<h2>Role Permission Matrix</h2>
							<div className="ns-card-desc">
								Control which user roles in your organisation receive each kind of notification alert.
							</div>

							<div className="ns-table-responsive">
								<table className="ns-matrix-table">
									<thead>
										<tr>
											<th>Role Name</th>
											<th>Inventory Alerts</th>
											<th>Sales Alerts</th>
											<th>Purchase Alerts</th>
											<th>Email Alerts</th>
											{/* Hidden: "Manage Settings" is not enforced anywhere on the backend. Who may edit
											    these settings is decided by the role permission
											    settings.notification_preferences.edit (Settings → Roles).
											<th>Manage Settings</th> */}
										</tr>
									</thead>
									<tbody>
										{availableRoles.map((role) => {
											const current = roleMatrix[role] || {};
											return (
												<tr key={role}>
													<td className="role-name">{role}</td>
													<td>
														<input
															type="checkbox"
															checked={!!current.inventory}
															onChange={() => handleToggleRolePermission(role, 'inventory')}
															disabled={!canEdit}
														/>
													</td>
													<td>
														<input
															type="checkbox"
															checked={!!current.sales}
															onChange={() => handleToggleRolePermission(role, 'sales')}
															disabled={!canEdit}
														/>
													</td>
													<td>
														<input
															type="checkbox"
															checked={!!current.purchase}
															onChange={() => handleToggleRolePermission(role, 'purchase')}
															disabled={!canEdit}
														/>
													</td>
													<td>
														<input
															type="checkbox"
															checked={!!current.email}
															onChange={() => handleToggleRolePermission(role, 'email')}
															disabled={!canEdit}
														/>
													</td>
													{/* Hidden — see the column header above.
													<td>
														<input
															type="checkbox"
															checked={!!current.manage_settings}
															onChange={() => handleToggleRolePermission(role, 'manage_settings')}
															disabled={!canEdit}
														/>
													</td> */}
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				)}

				{/* TAB 3: SCHEDULES & QUIET HOURS */}
				{activeTab === 'schedules' && (
					<div className="ns-schedules-view">
						{/* Quiet Hours Card */}
						<div className="ns-card mb-4">
							<div className="d-flex justify-content-between align-items-center mb-3">
								<div>
									<h2>Quiet Hours</h2>
									<div className="ns-card-desc mb-0">
										During quiet hours, non-critical alerts are buffered and delivered once the quiet window ends.
										{orgTimezone ? ` Times are in your organisation's time zone (${orgTimezone}).` : ''}
									</div>
								</div>
								<label className={`ns-toggle-switch ${!canEdit ? 'disabled' : ''}`}>
									<input
										type="checkbox"
										checked={!!quietHours.enabled}
										onChange={handleToggleQuietHours}
										disabled={!canEdit}
									/>
									<span className="ns-slider"></span>
								</label>
							</div>

							{quietHours.enabled && (
								<div className="row g-3 mt-1">
									<div className="col-md-3">
										<label className="form-label small fw-semibold text-secondary">
											Start Time
										</label>
										<input
											type="time"
											className="form-control form-control-sm"
											value={quietHours.start || '22:00'}
											onChange={(e) => {
												if (!canEdit) return;
												setQuietHours((prev) => ({ ...prev, start: e.target.value }));
												setHasChanges(true);
											}}
											disabled={!canEdit}
										/>
									</div>
									<div className="col-md-3">
										<label className="form-label small fw-semibold text-secondary">
											End Time
										</label>
										<input
											type="time"
											className="form-control form-control-sm"
											value={quietHours.end || '07:00'}
											onChange={(e) => {
												if (!canEdit) return;
												setQuietHours((prev) => ({ ...prev, end: e.target.value }));
												setHasChanges(true);
											}}
											disabled={!canEdit}
										/>
									</div>
								</div>
							)}
						</div>

						{/* Scheduled Jobs Table */}
						<div className="ns-card">
							<div className="d-flex justify-content-between align-items-center mb-3">
								<div>
									<h2>Scheduled Background Automation</h2>
									<div className="ns-card-desc mb-0">
										Each schedule is a master switch for the alerts listed under it. An alert is sent only while
										both its own switch (Notification Preferences) and its schedule are on.
									</div>
								</div>
							</div>

							<div className="ns-table-responsive">
								<table className="ns-data-table">
									<thead>
										<tr>
											<th>Job Name</th>
											<th>Status</th>
											<th>Frequency</th>
											<th>Trigger Time</th>
											<th>Last Run</th>
											<th>Next Run</th>
											<th>Quiet Hours / Digest</th>
											<th style={{ textAlign: 'right' }}>Actions</th>
										</tr>
									</thead>
									<tbody>
										{schedules.map((job) => (
											<tr key={job.id}>
												<td className="fw-semibold text-dark">
													{job.name}
													{(() => {
														const controlled = rules.filter((r) => r.scheduleManagedBy === job.rule_id);
														return controlled.length > 0 ? (
															<div className="ns-job-controls">
																Controls: {controlled.map((r) => r.title).join(', ')}
															</div>
														) : null;
													})()}
												</td>
												<td>
													{job.implemented === false ? (
														<span className="badge bg-secondary text-white" style={{ fontSize: '11px', padding: '4px 8px' }}>
															Not Implemented Yet
														</span>
													) : (
														<span
															className={`badge-status ${
																job.status === 'Enabled' ? 'sent' : 'muted'
															}`}
														>
															{job.status}
														</span>
													)}
													{job.implemented !== false &&
														job.status !== 'Enabled' &&
														(() => {
															const waiting = rules.filter(
																(r) => r.scheduleManagedBy === job.rule_id && r.enabled,
															).length;
															return waiting > 0 ? (
																<div className="ns-job-controls warn">
																	{waiting} enabled alert{waiting > 1 ? 's are' : ' is'} paused
																</div>
															) : null;
														})()}
												</td>
												<td>{job.frequency}</td>
												<td>{job.trigger_time}</td>
												<td>{job.implemented === false ? '—' : job.last_run}</td>
												<td>{job.implemented === false ? '—' : job.next_run}</td>
												<td className="text-secondary">{job.implemented === false ? '—' : (job.advanced || 'Standard')}</td>
												<td style={{ textAlign: 'right' }}>
													<div className="d-flex align-items-center justify-content-end gap-2">
														<label
															className={`ns-toggle-switch ${!canEdit || job.implemented === false ? 'disabled' : ''}`}
														>
															<input
																type="checkbox"
																checked={job.implemented === false ? false : job.status === 'Enabled'}
																onChange={() => handleToggleSchedule(job)}
																disabled={!canEdit || job.implemented === false}
																title={job.implemented === false ? 'This scheduled job is under development on our roadmap' : ''}
															/>
															<span className="ns-slider"></span>
														</label>
														<button
															type="button"
															className="ns-btn-configure"
															onClick={() => handleConfigureSchedule(job)}
														>
															Configure
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				)}

				{/* TAB: SNOOZED ALERTS */}
				{activeTab === 'snoozed' && (
					<div className="ns-history-view">
						<div className="ns-card">
							<div className="d-flex justify-content-between align-items-center mb-3">
								<div>
									<h2>Snoozed Alerts</h2>
									<div className="ns-card-desc mb-0">
										Batches whose expiry alerts are paused, for example from the Snooze buttons in an alert email. Alerts resume on their own after the date shown, or sooner if you press Unsnooze.
									</div>
								</div>
								<button
									type="button"
									className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
									onClick={loadSnoozed}
									disabled={loadingSnoozed}
								>
									<RefreshCw size={13} className={loadingSnoozed ? 'spin' : ''} />
									{loadingSnoozed ? 'Refreshing...' : 'Refresh'}
								</button>
							</div>

							{loadingSnoozed && snoozedBatches.length === 0 ? (
								<div className="text-center py-5 text-secondary">
									<div className="spinner-border spinner-border-sm me-2" role="status"></div>
									Loading snoozed alerts...
								</div>
							) : snoozedBatches.length === 0 ? (
								<div className="ns-empty-state py-4 text-center text-muted">
									No snoozed alerts. When you snooze a batch from an alert email, it will be listed here.
								</div>
							) : (
								<div className="ns-table-responsive">
									<table className="ns-data-table">
										<thead>
											<tr>
												<th>Product</th>
												<th>Batch</th>
												<th>Expiry Date</th>
												<th>Quantity</th>
												<th>Snoozed Until</th>
												<th>Snoozed By</th>
												{canEdit && <th>Action</th>}
											</tr>
										</thead>
										<tbody>
											{snoozedBatches.map((item) => {
												const daysLeft = Math.max(
													1,
													Math.ceil((new Date(item.snoozed_until) - new Date()) / 86400000),
												);
												return (
													<tr key={item.product_batch_id}>
														<td className="fw-semibold text-dark">{item.product_name}</td>
														<td>{item.batch_no || '-'}</td>
														<td>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-GB', { timeZone: 'UTC' }) : '-'}</td>
														<td>{item.quantity ?? '-'}</td>
														<td>
															{new Date(item.snoozed_until).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', ...(orgTimezone ? { timeZone: orgTimezone } : {}) })}
															<span className="text-secondary"> ({daysLeft} {daysLeft === 1 ? 'day' : 'days'} left)</span>
														</td>
														<td className="text-secondary">
															{item.snoozed_by || '-'}
															{item.snoozed_at && <div className="small">{formatTime(item.snoozed_at)}</div>}
														</td>
														{canEdit && (
															<td>
																<button
																	type="button"
																	className="btn btn-sm btn-outline-primary"
																	onClick={() => handleUnsnooze(item)}
																	disabled={unsnoozingId === item.product_batch_id}
																>
																	{unsnoozingId === item.product_batch_id ? 'Resuming...' : 'Unsnooze'}
																</button>
															</td>
														)}
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>
				)}

				{/* TAB 4: HISTORY */}
				{activeTab === 'history' && (
					<div className="ns-history-view">
						<div className="ns-card">
							<div className="d-flex justify-content-between align-items-center mb-3">
								<div>
									<h2>Recent Notification History</h2>
									<div className="ns-card-desc mb-0">
										Audit trail of recent dispatched, queued, and suppressed alerts across your organisation.
									</div>
								</div>
								<button
									type="button"
									className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
									onClick={loadHistory}
									disabled={loadingHistory}
								>
									<RefreshCw size={13} className={loadingHistory ? 'spin' : ''} />
									{loadingHistory ? 'Refreshing...' : 'Refresh History'}
								</button>
							</div>

							{loadingHistory && historyLogs.length === 0 ? (
								<div className="text-center py-5 text-secondary">
									<div className="spinner-border spinner-border-sm me-2" role="status"></div>
									Loading notification history...
								</div>
							) : historyLogs.length === 0 ? (
								<div className="ns-empty-state py-4 text-center text-muted">
									No notification events recorded yet. Dispatched or suppressed alerts will appear here.
								</div>
							) : (
								<div className="ns-table-responsive">
									<table className="ns-data-table">
										<thead>
											<tr>
												<th>Timestamp</th>
												<th>Event / Alert</th>
												<th>Module</th>
												<th>Channel</th>
												<th>Recipients</th>
												<th>Status</th>
											</tr>
										</thead>
										<tbody>
											{historyLogs.map((item) => (
												<tr key={item.id}>
													<td className="text-secondary">{formatTime(item.time)}</td>
													<td className="fw-semibold text-dark">{item.event}</td>
													<td>{item.module}</td>
													<td className="text-capitalize">{item.channel}</td>
													<td>{item.recipients}</td>
													<td>
														<span
															className={`badge-status ${
																item.status?.toLowerCase() || 'muted'
															}`}
														>
															{(item.status || 'UNKNOWN').toUpperCase()}
															{item.reason && ` (${item.reason})`}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>
				)}

				{/* CONFIGURE SLIDE-OUT DRAWER */}
				{drawerRule && (
					<div
						className="ns-drawer-backdrop"
						onClick={() => setDrawerRule(null)}
					>
						<div
							className="ns-drawer"
							onClick={(e) => e.stopPropagation()}
						>
							{/* Drawer Header */}
							<div className="ns-drawer-header">
								<div>
									<div className="module-tag">{drawerRule.module}</div>
									<h2>{drawerRule.title}</h2>
									{drawerRule.description && (
										<p className="text-secondary small mt-1 mb-0">
											{drawerRule.description}
										</p>
									)}
								</div>
								<button
									className="btn-close-drawer"
									onClick={() => setDrawerRule(null)}
									disabled={drawerSaving}
								>
									<X size={20} />
								</button>
							</div>

							{/* Drawer Body */}
							<div className="ns-drawer-body">
								{drawerRule.implemented === false && (
									<div
										className="alert alert-secondary d-flex align-items-center gap-2 mb-3 py-2 px-3"
										style={{ fontSize: '13px' }}
									>
										<AlertCircle size={16} className="text-secondary flex-shrink-0" />
										<div>
											<strong>Not Implemented Yet:</strong> This notification routine is on our product roadmap. It cannot be configured or dispatched yet.
										</div>
									</div>
								)}

								{/* Status */}
								<div className="ns-form-group">
									<div className="drawer-switch-row">
										<label className="mb-0">Enable this notification</label>
										<label className={`ns-toggle-switch ${!canEdit || drawerSaving || drawerRule.implemented === false ? 'disabled' : ''}`}>
											<input
												type="checkbox"
												checked={drawerRule.implemented === false ? false : !!drawerRule.enabled}
												onChange={(e) => {
													if (drawerRule.implemented === false) return;
													handleUpdateDrawerRule({ enabled: e.target.checked });
												}}
												disabled={!canEdit || drawerSaving || drawerRule.implemented === false}
											/>
											<span className="ns-slider"></span>
										</label>
									</div>
								</div>

								{/* Channel Selection */}
								<div className="ns-form-group">
									<label>Delivery Channel</label>
									<div className="drawer-pills">
										{!drawerRule.lockedChannel && (
											<button
												type="button"
												className={drawerRule.channel === 'in_app' ? 'active' : ''}
												onClick={() => handleUpdateDrawerRule({ channel: 'in_app' })}
												disabled={!canEdit || drawerSaving}
											>
												In-app
											</button>
										)}
										{(!drawerRule.lockedChannel || drawerRule.lockedChannel === 'email') && (
											<button
												type="button"
												className={drawerRule.channel === 'email' ? 'active' : ''}
												onClick={() => handleUpdateDrawerRule({ channel: 'email' })}
												disabled={!canEdit || drawerSaving || !!drawerRule.lockedChannel}
											>
												Email
											</button>
										)}
										{!drawerRule.lockedChannel && (
											<button
												type="button"
												className={drawerRule.channel === 'both' ? 'active' : ''}
												onClick={() => handleUpdateDrawerRule({ channel: 'both' })}
												disabled={!canEdit || drawerSaving}
											>
												Both
											</button>
										)}
									</div>
								</div>

								{/* Target Roles */}
								<div className="ns-form-group">
									<label>Target Roles</label>
									<div className="roles-checkbox-grid">
										{availableRoles.map((role) => {
											const currentRoles = (drawerRule.defaultRoles || []).map((r) => {
												const lower = String(r).toLowerCase();
												if (lower.includes('owner')) return 'Organisation Owner';
												if (lower.includes('manager') || lower.includes('admin')) return 'Manager';
												if (lower.includes('accountant')) return 'Accountant';
												if (lower.includes('staff') || lower.includes('pharmacist')) return 'Staff';
												return r;
											});
											const isChecked = currentRoles.includes(role);

											return (
												<label key={role}>
													<input
														type="checkbox"
														checked={isChecked}
														disabled={!canEdit || drawerSaving}
														onChange={(e) => {
															const nextRoles = e.target.checked
																? [...new Set([...currentRoles, role])]
																: currentRoles.filter((r) => r !== role);
															handleUpdateDrawerRule({ defaultRoles: nextRoles });
														}}
													/>
													{role}
												</label>
											);
										})}
									</div>
								</div>

								{/* Trigger Type: choose Instant on Event vs Scheduled Routine Only vs Both */}
								{!/^.+_job$/.test(drawerRule.id || '') && (
									<div className="ns-form-group">
										<label>Trigger Type</label>
										<select
											value={drawerRule.triggerType || 'immediate'}
											onChange={(e) =>
												handleUpdateDrawerRule({ triggerType: e.target.value })
											}
											disabled={!canEdit || drawerSaving}
										>
											<option value="immediate">Instant on Event</option>
											<option value="scheduled">Scheduled Routine Only</option>
											<option value="both">Both (Instant & Scheduled)</option>
										</select>
									</div>
								)}

								{/* Trigger Time: available across ALL notification settings */}
								<div className="ns-form-group">
									<label>Trigger Time</label>
									<select
										value={normalizeTimeValue(drawerRule.triggerTime)}
										onChange={(e) =>
											handleUpdateDrawerRule({ triggerTime: e.target.value })
										}
										disabled={!canEdit || drawerSaving}
									>
										{!TRIGGER_TIME_OPTIONS.some(
											(opt) => opt.value === normalizeTimeValue(drawerRule.triggerTime),
										) && (
											<option value={normalizeTimeValue(drawerRule.triggerTime)}>
												{normalizeTimeValue(drawerRule.triggerTime)}
											</option>
										)}
										{TRIGGER_TIME_OPTIONS.map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
									</select>
								</div>

								{/* Schedule Frequency & Day: for Scheduled Routine Only, Both, or background Jobs */}
								{(drawerRule.triggerType === 'scheduled' ||
									drawerRule.triggerType === 'both' ||
									/^.+_job$/.test(drawerRule.id || '')) && (
									<>
										<div className="ns-form-group">
											<label>Frequency</label>
											<select
												value={drawerRule.frequency?.toLowerCase() || 'daily'}
												onChange={(e) =>
													handleUpdateDrawerRule({ frequency: e.target.value })
												}
												disabled={!canEdit || drawerSaving}
											>
												<option value="hourly">Hourly</option>
												<option value="daily">Daily</option>
												<option value="weekly">Weekly</option>
											</select>
										</div>

										{drawerRule.frequency?.toLowerCase() === 'weekly' && (
											<div className="ns-form-group">
												<label>Day of the Week</label>
												<select
													value={Number.isInteger(drawerRule.weekday) ? drawerRule.weekday : 1}
													onChange={(e) =>
														handleUpdateDrawerRule({ weekday: Number(e.target.value) })
													}
													disabled={!canEdit || drawerSaving}
												>
													{['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(
														(d, i) => (
															<option key={d} value={i}>
																{d}
															</option>
														),
													)}
												</select>
											</div>
										)}
									</>
								)}

								{/* Cooldown Period: correlated with Trigger Time & Schedule */}
								<div className="ns-form-group">
									<label>Cooldown Period</label>
									<select
										value={drawerRule.cooldown?.toLowerCase() || 'none'}
										onChange={(e) =>
											handleUpdateDrawerRule({ cooldown: e.target.value })
										}
										disabled={!canEdit || drawerSaving}
									>
										{getCorrelatedCooldownOptions(drawerRule).map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
									</select>
									{(drawerRule.triggerType === 'scheduled' ||
										drawerRule.triggerType === 'both' ||
										/^.+_job$/.test(drawerRule.id || '')) && (
										<div className="text-secondary mt-1" style={{ fontSize: '11px' }}>
											{drawerRule.frequency?.toLowerCase() === 'hourly' &&
												'Cooldown is limited to 1 hour to align with hourly schedule.'}
											{(drawerRule.frequency?.toLowerCase() === 'daily' || !drawerRule.frequency) &&
												'Cooldown is limited to 24 hours / 3 days to align with daily schedule.'}
											{drawerRule.frequency?.toLowerCase() === 'weekly' &&
												'Cooldown is limited to 7 days to align with weekly schedule.'}
										</div>
									)}
								</div>

								{/* Notice Period (Days Before Due Date / Expiry) Dropdown */}
								{(Array.isArray(drawerRule.leadDays) ||
									[
										'near_expiry',
										'payment_due_reminder',
										'vendor_payment_due_reminder',
										'payment_due_job',
										'vendor_payment_due_job',
									].includes(drawerRule.id)) && (() => {
									const isExpiry = String(drawerRule.id || '').toLowerCase().includes('expiry');
									const currentVal =
										Array.isArray(drawerRule.leadDays) && drawerRule.leadDays.length
											? [...drawerRule.leadDays].sort((a, b) => b - a).join(',')
											: isExpiry
											? '30,15,7'
											: '3,2,1';

									const standardExpiryOptions = [
										{ value: '30,15,7', label: '30, 15, 7 days before expiry' },
										{ value: '60,30,15', label: '60, 30, 15 days before expiry' },
										{ value: '90,60,30', label: '90, 60, 30 days before expiry' },
										{ value: '180,90,30', label: '180, 90, 30 days before expiry' },
										{ value: '30', label: '30 days before expiry' },
										{ value: '15', label: '15 days before expiry' },
										{ value: '7', label: '7 days before expiry' },
										{ value: '3', label: '3 days before expiry' },
										{ value: '1', label: '1 day before expiry' },
									];

									const standardDueDateOptions = [
										{ value: '3,2,1', label: '3, 2, 1 days before due date' },
										{ value: '7,3,1', label: '7, 3, 1 days before due date' },
										{ value: '14,7,3', label: '14, 7, 3 days before due date' },
										{ value: '30,15,7', label: '30, 15, 7 days before due date' },
										{ value: '14', label: '14 days before due date' },
										{ value: '7', label: '7 days before due date' },
										{ value: '3', label: '3 days before due date' },
										{ value: '1', label: '1 day before due date' },
									];

									const options = isExpiry ? standardExpiryOptions : standardDueDateOptions;
									const isCustom = !options.some((opt) => opt.value === currentVal);

									return (
										<div className="ns-form-group">
											<label>Advance Notice (Days Before {isExpiry ? 'Expiry' : 'Due Date'})</label>
											<select
												value={currentVal}
												onChange={(e) => {
													const val = e.target.value;
													const days = val
														? val
																.split(',')
																.map((v) => parseInt(v.trim(), 10))
																.filter((n) => !isNaN(n))
														: [];
													handleUpdateDrawerRule({ leadDays: days });
												}}
												disabled={!canEdit || drawerSaving}
											>
												{options.map((opt) => (
													<option key={opt.value} value={opt.value}>
														{opt.label}
													</option>
												))}
												{isCustom && (
													<option value={currentVal}>
														{formatLeadDaysLabel(drawerRule.leadDays, drawerRule.id)}
													</option>
												)}
											</select>
											<div className="text-secondary mt-1" style={{ fontSize: '11px' }}>
												{isExpiry
													? 'Determines how many days in advance this alert will trigger before batch expiry.'
													: 'Determines how many days in advance this alert will trigger before payment due date.'}
											</div>
										</div>
									);
								})()}

								{/* Priority / Severity */}
								<div className="ns-form-group">
									<label>Alert Severity / Priority</label>
									<select
										value={(drawerRule.priority || drawerRule.severity || 'info').toLowerCase()}
										onChange={(e) =>
											handleUpdateDrawerRule({
												priority: e.target.value,
												severity: e.target.value,
											})
										}
										disabled={!canEdit || drawerSaving}
									>
										<option value="info">Info</option>
										<option value="warning">Warning</option>
										<option value="critical">Critical</option>
									</select>
								</div>

								{/* Digest Mode */}
								<div className="ns-form-group">
									<div className="drawer-switch-row">
										<div>
											<label className="mb-0">Digest Mode</label>
											<div className="text-secondary" style={{ fontSize: '12px' }}>
												Group multiple events into a single batched notification.
											</div>
										</div>
										<label className={`ns-toggle-switch ${!canEdit || drawerSaving ? 'disabled' : ''}`}>
											<input
												type="checkbox"
												checked={!!drawerRule.digestMode}
												onChange={(e) =>
													handleUpdateDrawerRule({ digestMode: e.target.checked })
												}
												disabled={!canEdit || drawerSaving}
											/>
											<span className="ns-slider"></span>
										</label>
									</div>
								</div>
							</div>

							{/* Drawer Footer */}
							<div className="ns-drawer-footer">
								{showTestAlert && (
									<button
										type="button"
										className="btn-drawer-test me-auto"
										onClick={() => handleTestTrigger(drawerRule.id)}
										disabled={drawerRule.implemented === false || testingRuleId === drawerRule.id || drawerSaving}
										title={drawerRule.implemented === false ? 'Feature in development — cannot test yet' : ''}
									>
										<Bell size={14} />
										{testingRuleId === drawerRule.id ? 'Sending Alert...' : 'Test This Alert'}
									</button>
								)}
								<button
									className="btn-cancel"
									onClick={() => setDrawerRule(null)}
									disabled={drawerSaving}
								>
									{canEdit ? 'Cancel' : 'Close'}
								</button>
								{canEdit && (
									<button
										className="btn-save"
										onClick={handleApplyDrawerRule}
										disabled={drawerRule.implemented === false || drawerSaving}
										title={drawerRule.implemented === false ? 'Cannot save an unimplemented routine' : ''}
									>
										{drawerSaving ? 'Saving...' : 'Apply & Save'}
									</button>
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
